import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { QuestionDTO, QuestionSetDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useQuestionApi } from '../../hooks/useQuestionApi';
import QuestionTable from '../tables/QuestionTable';
import AddNewButton from '../common/AddButton';
import { useNavigate } from 'react-router-dom';
import { confirmDelete } from '../../utils/sweetAlertUtils';

interface QuestionSetFormProps {
  initialValues: QuestionSetDTO;
  onSubmit: (data: QuestionSetDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
  questions: {
    id: 'questions',
    title: 'Questions',
  },
} as const;

const getFormSections = (hasId: boolean) => {
  const baseSections = {
    basicInfo: FORM_SECTIONS.basicInfo,
    settings: FORM_SECTIONS.settings,
  } as const;

  if (hasId) {
    return {
      ...baseSections,
      questions: FORM_SECTIONS.questions,
    } as const;
  }

  return baseSections;
};

const QuestionSetForm: React.FC<QuestionSetFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<QuestionSetDTO>(initialValues);
  const { search, remove } = useQuestionApi();
  const [questions, setQuestions] = useState<QuestionDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const navigate = useNavigate();

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    questionsToShow: [
      { required: true, message: 'No of questions to show is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'No of questions to show must be a positive number',
      },
    ],
    displayPosition: [
      { required: true, message: 'Display Position is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Display Position must be a positive number',
      },
    ],
  };

  const { errors, validate } =
    useFormValidation<QuestionSetDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const handleDelete = async (dto: QuestionDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchQuestions(currentPage, pageSize);
    } catch (error) {
      console.error('Error deleting Question:', error);
    }
  };

  const fetchQuestions = async (page: number, size: number) => {
    try {
      const response = await search({
        filters: { questionSetId: initialValues.id },
        page,
        pageSize: size,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setQuestions(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  useEffect(() => {
    if (initialValues.id) {
      fetchQuestions(currentPage, pageSize);
    }
  }, [currentPage, pageSize, initialValues.id]);

  const sections = Object.values(getFormSections(Boolean(formData.id)));

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={true}
          error={errors.title}
        />
        <FormField
          id="questionsToShow"
          label="No of questions to show"
          type="number"
          value={formData.questionsToShow || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter No of questions to show"
          required={true}
          error={errors.questionsToShow}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="displayPosition"
          label="Display Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Display Position"
          required={true}
          error={errors.displayPosition}
        />
        <FormField
          id="isActive"
          label="Is Active"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Is Active"
          required={false}
          error={errors.isActive}
        />
      </FormSection>
      {formData.id && (
        <FormSection
          id={FORM_SECTIONS.questions.id}
          title={FORM_SECTIONS.questions.title}
          headerActions={
            <AddNewButton
              variant="secondary"
              label="Add New Question"
              onClick={() =>
                navigate(`/admin/questionsets/${formData.id}/questions/create`)
              }
            />
          }
          columns={1}
        >
          <div className="mb-4">
            <QuestionTable
              data={questions}
              handleEdit={(dto) =>
                navigate(
                  `/admin/questionsets/${formData.id}/questions/${dto.id}`,
                )
              }
              handleDelete={handleDelete}
              currentPage={currentPage}
              totalPages={Math.ceil(totalRecords / pageSize)}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              isLoading={false}
            />
          </div>
        </FormSection>
      )}
    </Form>
  );
};

export default QuestionSetForm;
